import _ from "lodash";
import axios from "axios";
import client from "lib/apolloClient/client";
import update from "immutability-helper";
import ACLStore from "lib/aclStore";

// constants

import {
  ASSESSMENT,
  UNIT,
  COURSE_BASED_GRADING,
} from "Constants/stringConstants";
import {
  UNIT_DEPENDENT_LEARNING_GOALS,
  MYP_OBJECTIVES,
  REQUIRED_LEARNING_GOALS_FIELDS_UIDS,
  TEMP_ID,
  CURRICULUM_SORT_ORDERS,
} from "./Constants";

// utils
import {
  getFieldValue,
  getExcludedFieldsForAI,
  getModifiedContextFields,
  getIsMYPInterdisciplinaryUnit,
  getPlannerElementLabel,
} from "Assessments/modules/Utils";
import {
  getEntityAndFieldType,
  getContextFieldValue,
  updateIsRequired,
  getIsContextFieldDisabled,
  getDefaultContextFields,
  getContextFieldDisabledTooltip,
} from "./Utils";
import { STANDARD_UIDS } from "modules/Services/ServicesConstants";
import {
  getAcademicSetupType,
  getIsLearningCoursesEnabled,
} from "Teacher/routes/LearningCourses/modules/Utils";
import { getIsNewGradingSetupEnabled } from "ClassRoom/modules/utils";

// modules
import { isUnitPlanEditPermission } from "UnitPlans/modules/UnitPlanModule";
import {
  createAttachmentsMutation,
  deleteAttachmentMutation,
  updateAttachmentMutation,
} from "modules/CommonMutations";
import {
  getAIConversationsFromCache,
  writeAIConversationsInCache,
} from "ClassRoom/components/AddAssignment/LessonPlanningAssistant/modules/LessonPlanningGraphqlHelpers";
import { generateRandomId } from "services/generateRandomId";
import { getUserId } from "modules/CommonSelectors";
import { getIsConfigurationEnabledForCPAY } from "modules/PermissionModule";
import { USE_NEW_GRADING_SETUP_IN_COURSE_PERM } from "Constants/permissionConstants";

/*
  Case 1 -> When no predefined prompt is selected then context will be
    1. Default context fields
    2. Learning goals fields

  Case 2 -> When predefined prompt is selected then context will be
    1. promptFields defined with the selected prompt
    2. Learning goals fields
*/

const _getPlannerElementLabel = params => {
  const { t, field, isLearningCoursesEnabled } = params;

  if (
    field === "learningStandardUBD" &&
    isLearningCoursesEnabled &&
    getIsConfigurationEnabledForCPAY({
      configuration: USE_NEW_GRADING_SETUP_IN_COURSE_PERM,
    })
  ) {
    return t("common:standard_plural");
  }

  return getPlannerElementLabel(params);
};

export const getContextFieldsWithValue = params => {
  return (dispatch, getState) => {
    const {
      t,
      context,
      template,
      selectedAction,
      assessmentTemplate,
      unitPlanTemplate,
      assessmentDetails,
      unitPlanDetails,
      curriculumProgramType,
      excludedFields,
      conversationMetadata,
      isQuestionAssistant,
      isBlankQuestion,
      prompts = [],
      isAITutor,
      templateId,
    } = params;

    const academicSetupType = getAcademicSetupType();
    const isCourseBasedGrading = academicSetupType === COURSE_BASED_GRADING;
    const isLearningCoursesEnabled = getIsLearningCoursesEnabled();

    const plannerElementSets = _.get(
      getState(),
      "platform.currentPlannerElementSets",
      {}
    );

    const plannerElementLabelCommonArgs = {
      t,
      templateId,
      curriculumType: curriculumProgramType,
      plannerElementSets,
    };

    const { promptFields = [] } = selectedAction || {};

    let resultFields = [];

    const metadataPromptFields = _.get(
      conversationMetadata,
      "promptFields",
      []
    );

    const assessmentFieldList = _.get(assessmentTemplate, "field_list", {});
    const unitPlanFieldList = _.get(unitPlanTemplate, "field_list", {});
    const unitPlanStepList = _.get(unitPlanTemplate, "step_list", []);

    const unitPlanFields = _.get(unitPlanDetails, "unitPlanFields", []);

    // curriculum default context fields
    const defaultContextFields = getDefaultContextFields({
      t,
      isAITutor,
      isBlankQuestion,
      isQuestionAssistant,
      curriculumProgramType,
    });

    // curriculum assessment learning goals fields
    const learningGoalsFields = _.chain(assessmentFieldList)
      .get("addLearningGoals.config.options")
      .map(({ key }) => {
        let isRequired = false;
        if (isQuestionAssistant && isBlankQuestion) {
          isRequired = _.includes(REQUIRED_LEARNING_GOALS_FIELDS_UIDS, key);
        }
        return { field: key, type: ASSESSMENT, isRequired };
      })
      .sortBy(item => {
        const sortOrder = CURRICULUM_SORT_ORDERS[curriculumProgramType];
        return sortOrder ? _.indexOf(sortOrder, item?.field) : 0;
      })
      .value();

    // if user has not selected any predefined prompt
    if (
      isAITutor ||
      _.isEmpty(selectedAction) ||
      !_.isEmpty(metadataPromptFields)
    ) {
      if (!_.isEmpty(metadataPromptFields)) {
        resultFields = getModifiedContextFields(metadataPromptFields);
      } else {
        const combinedPrompts = _.concat(
          defaultContextFields,
          learningGoalsFields
        );

        // support for old and new prompt fields structure
        resultFields = getModifiedContextFields(combinedPrompts);
      }

      resultFields = _.map(resultFields, resultField => {
        const { config, isRequired } = resultField || {};
        const { label: configLabel, isLocalised } = config || {};

        const { field, type } = getEntityAndFieldType({
          ...resultField,
          unitPlanDetails,
          assessmentDetails,
        });

        const step =
          field === "inquiryQuestions" && type === UNIT ? "IQ" : null;

        const fieldList =
          type === ASSESSMENT ? assessmentFieldList : unitPlanFieldList;

        const fieldConfig = _.get(fieldList, `${field}.config`);
        const { viewLabel, label, viewLabelLocale } = fieldConfig || {};

        const plannerElementLabel = _getPlannerElementLabel({
          ...plannerElementLabelCommonArgs,
          t,
          field,
          viewLabel,
          viewLabelLocale,
          plannerElementType: _.get(fieldConfig, "plannerElementConfig.type"),
          isLearningCoursesEnabled,
        });

        let value = null,
          tooltip = null;

        // for step, join the values of the step fields
        if (step) {
          const stepFields = _.get(unitPlanStepList, `${step}.fields`, []);

          if (!_.isEmpty(stepFields)) {
            const fieldValues = _.map(stepFields, stepField => {
              const { value } = getContextFieldValue({
                field: stepField,
                type,
                ...params,
              });

              return value;
            });

            value = _.join(
              _.filter(fieldValues, value => !_.isEmpty(_.trim(value))),
              ", "
            );
          }
        } else {
          const {
            value: fieldValue,
            tooltip: fieldTooltip,
          } = getContextFieldValue({ field, type, ...params });

          value = fieldValue;
          tooltip = fieldTooltip;
        }

        const isDisabled = getIsContextFieldDisabled({
          ...resultField,
          ...params,
          field,
          value,
        });

        const disabledTooltip = getContextFieldDisabledTooltip({
          ...resultField,
          ...params,
          field,
        });

        return {
          field,
          type,
          label:
            plannerElementLabel ||
            viewLabel ||
            label ||
            (isLocalised ? t(configLabel) : configLabel),
          value,
          tooltip,
          isRequired,
          config,
          step,
          isDisabled,
          disabledTooltip,
        };
      });
    } else {
      let combinedPrompts = _.concat(promptFields, learningGoalsFields);

      if (isQuestionAssistant) {
        combinedPrompts = _.concat(defaultContextFields, combinedPrompts);
      }

      resultFields = getModifiedContextFields(combinedPrompts);

      resultFields = _.reduce(
        resultFields,
        (acc, resultField) => {
          const { config, isRequired = true } = resultField || {};
          const { label: configLabel, isLocalised } = config || {};

          const { field, type } = getEntityAndFieldType({
            ...resultField,
            unitPlanDetails,
            assessmentDetails,
          });

          // TODO: take from template
          const step =
            field === "inquiryQuestions" && type === UNIT ? "IQ" : null;

          const fieldList =
            type === ASSESSMENT ? assessmentFieldList : unitPlanFieldList;

          const fieldConfig = _.get(fieldList, `${field}.config`);
          const { viewLabel, label, viewLabelLocale } = fieldConfig || {};

          const plannerElementLabel = _getPlannerElementLabel({
            ...plannerElementLabelCommonArgs,
            t,
            field,
            viewLabel,
            viewLabelLocale,
            plannerElementType: _.get(fieldConfig, "plannerElementConfig.type"),
            isLearningCoursesEnabled,
          });

          const { value, tooltip } = getContextFieldValue({
            field,
            type,
            config,
            context,
            ...params,
          });

          const isDisabled = getIsContextFieldDisabled({
            ...resultField,
            ...params,
            field,
            value,
          });

          const disabledTooltip = getContextFieldDisabledTooltip({
            ...resultField,
            ...params,
            field,
          });

          if (
            _.includes(UNIT_DEPENDENT_LEARNING_GOALS, field) &&
            type === ASSESSMENT &&
            isRequired
          ) {
            const { value: unitPlanFieldValue, tooltip } = getContextFieldValue(
              {
                field,
                type: UNIT,
                config,
                context,
                ...params,
              }
            );

            if (_.isEmpty(unitPlanFieldValue)) {
              const {
                viewLabel: unitFieldViewLabel,
                label: unitFieldLabel,
              } = _.get(unitPlanFieldList, `${field}.config`, {});

              acc.push({
                field,
                type: UNIT,
                label: `${t("common:unit")} ${_.lowerCase(
                  unitFieldViewLabel || unitFieldLabel || viewLabel || label
                )}`,
                value: unitPlanFieldValue,
                tooltip,
                isRequired,
                step: field === "inquiryQuestions" ? "IQ" : null,
                isDisabled,
                disabledTooltip,
              });
            }
          }

          acc.push({
            field,
            type,
            label:
              plannerElementLabel ||
              viewLabel ||
              label ||
              (isLocalised ? t(configLabel) : configLabel),
            value,
            tooltip,
            config,
            isRequired,
            step,
            isDisabled,
            disabledTooltip,
          });

          return acc;
        },
        []
      );
    }

    const unitPlanId = getFieldValue({
      fields: assessmentDetails?.allFields,
      uid: "unitPlan",
    });

    const canEditUnitPlan =
      _.isEmpty(unitPlanId) ||
      dispatch(isUnitPlanEditPermission({ unitPlanId })) ||
      ACLStore.can("TeacherPortal:CanEditUnitPlan");

    let fieldsToExclude = getExcludedFieldsForAI({
      curriculumProgramType,
      unitPlanFields,
      assessmentDetails,
      unitPlanTemplate,
      excludedFields,
      template,
      prompts,
    });

    // edge case : if both myp subject specific and IDU objects are in prompt fields and both of them are required
    // then on selecting one of them make the other one non-required
    if (
      getIsMYPInterdisciplinaryUnit(unitPlanFields) &&
      !getIsNewGradingSetupEnabled()
    ) {
      fieldsToExclude = _.filter(
        fieldsToExclude,
        field => !_.includes(MYP_OBJECTIVES, field)
      );

      resultFields = updateIsRequired(resultFields);
    }

    // this will filter input variables like ageGroup, grade etc.
    resultFields = _.filter(resultFields, resultField => {
      const {
        field,
        type,
        label,
        value,
        isRequired,
        config = {},
        isDisabled = false,
      } = resultField || {};
      const { hideIfNotRequired = false } = config || {};

      const isStandardField = _.includes(_.values(STANDARD_UIDS), field);

      return (
        type &&
        label &&
        (type !== UNIT || canEditUnitPlan) &&
        !_.includes(fieldsToExclude, field) &&
        !(hideIfNotRequired && !isRequired) &&
        // if field is disabled then it should have value (as user can't select it)
        // EXCEPT for standard fields which should be shown with disabled state and tooltip
        (!isDisabled || !_.isEmpty(value) || isStandardField) &&
        // hide subjects field if course based grading is enabled
        !(field === "subjects" && isCourseBasedGrading)
      );
    });

    // show required fields at top;
    resultFields = _.orderBy(resultFields, "isRequired", "desc");

    resultFields = _.uniqBy(resultFields, field => {
      const { field: fieldUID, type } = field || {};
      return `${fieldUID}_${type}`;
    });

    return resultFields;
  };
};

const extractTitleFromText = text => {
  const titleRegex = /Video Title:\s*(.+)/i;
  const match = text.match(titleRegex);
  return match ? match[1].trim() : null;
};

export const createAIConversationAttachments = params => {
  const { input, aiConversationId, updateTitleAfterCreation = false } = params;

  return async (dispatch, getState) => {
    const conversationData = getAIConversationsFromCache({
      id: aiConversationId,
    });

    try {
      const userId = getUserId(getState());

      const attachments = _.map(input, attachment => {
        return {
          id: `${TEMP_ID}${generateRandomId()}`,
          ...attachment,
          name: !_.isEmpty(attachment?.name) ? attachment?.name : "Untitled",
          thumbUrl: null,
          title: null,
          streamUrl: null,
          signedUrl: null,
          parentType: "AI_CONVERSATION",
          metadata: null,
          isRead: true,
          similarityReport: null,
          lockingState: null,
          questionAttachment: null,
          createdBy: {
            id: userId,
            type: "staff",
            __typename: "Staff",
          },
          __typename: "Attachment",
        };
      });

      pushNewAttachmentToCache({
        aiConversationId,
        attachments,
      });

      const result = await client.mutate({
        mutation: createAttachmentsMutation,
        variables: { input },
        update: (
          cache,
          {
            data: {
              platform: { createAttachment },
            },
          }
        ) => {
          pushNewAttachmentToCache({
            aiConversationId,
            attachments: createAttachment,
          });
        },
      });

      const createdAttachments = _.get(
        result,
        "data.platform.createAttachment",
        []
      );

      // When url is from YouTube, the scraped name is "- YouTube",
      //  so we are getting the original video title and updating it
      if (updateTitleAfterCreation) {
        const attachment = _.get(createdAttachments, "0");
        const { type, name, metadata } = attachment || {};

        if (type === "LINK" && name === "- YouTube") {
          const extractedContentDetails = _.get(
            metadata,
            "extractedContentDetails"
          );
          const url = _.get(extractedContentDetails, "url");

          if (url) {
            try {
              // Fetch the data from the URL
              const response = await axios.get(url);
              const fetchedText = response.data;

              const title = extractTitleFromText(fetchedText);

              if (title) {
                // Prepare input for the update mutation
                const updateInput = {
                  id: attachment.id,
                  name: title,
                };

                await dispatch(
                  updateAIConversationAttachment({
                    input: updateInput,
                    aiConversationId,
                  })
                );

                // udpate the title for the created attachment
                const updatedAttachments = update(createdAttachments, {
                  [0]: {
                    name: { $set: title },
                  },
                });

                return updatedAttachments;
              }
            } catch (err) {}
          }
        }
      }

      return createdAttachments;
    } catch (err) {
      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: conversationData,
        },
      });
    }
  };
};

export const deleteAIConversationAttachment = ({ input, aiConversationId }) => {
  return async (dispatch, getState) => {
    const conversationData = getAIConversationsFromCache({
      id: aiConversationId,
    });

    try {
      const { attachmentIds } = input || {};

      const updatedConversationData = {
        ...(conversationData || {}),
        attachments: _.filter(
          conversationData?.attachments,
          attachment => !_.includes(attachmentIds, _.get(attachment, "id"))
        ),
        __typename: "AiConversation",
      };

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversationData,
        },
      });

      return await client.mutate({
        mutation: deleteAttachmentMutation,
        variables: { attachmentIds },
      });
    } catch (err) {
      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: conversationData,
        },
      });
    }
  };
};

export const updateAIConversationAttachment = ({ input, aiConversationId }) => {
  return async (dispatch, getState) => {
    const conversationData = getAIConversationsFromCache({
      id: aiConversationId,
    });

    try {
      const { id: attachmentId, ...rest } = input || {};

      const updatedConversationData = {
        ...conversationData,
        attachments: _.map(conversationData?.attachments, attachment => {
          if (attachment.id === attachmentId) {
            return { ...attachment, ...rest };
          }

          return attachment;
        }),
        __typename: "AiConversation",
      };

      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: updatedConversationData,
        },
      });

      return await client.mutate({
        mutation: updateAttachmentMutation,
        variables: { input },
      });
    } catch (e) {
      writeAIConversationsInCache({
        variables: {
          id: aiConversationId,
        },
        data: {
          node: conversationData,
        },
      });
    }
  };
};

export const pushNewAttachmentToCache = params => {
  const { aiConversationId, attachments } = params;

  const conversationData = getAIConversationsFromCache({
    id: aiConversationId,
  });

  let updatedConversationData = conversationData;

  const conversationAttachments = _.get(conversationData, "attachments", []);

  if (_.some(conversationAttachments, ({ id }) => _.includes(id, TEMP_ID))) {
    const filteredAttachments = _.filter(
      conversationAttachments,
      ({ id }) => !_.includes(id, TEMP_ID)
    );

    const newAttachments = [...filteredAttachments, ...attachments];

    updatedConversationData = update(conversationData, {
      attachments: {
        $set: newAttachments,
      },
    });
  } else {
    updatedConversationData = update(conversationData, {
      attachments: {
        $push: attachments,
      },
    });
  }

  writeAIConversationsInCache({
    variables: {
      id: aiConversationId,
    },
    data: {
      node: updatedConversationData,
    },
  });
};
